import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Minus, Trash2, ShoppingCart, Info } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const inr = (n) => "\u20B9" + n.toLocaleString("en-IN");

const ServiceBookingDialog = ({ service, open, onOpenChange }) => {
  const [qty, setQty] = useState({});

  const items = service?.booking || [];

  // Reset the cart whenever a different service is opened (prevents qty leaking across services)
  useEffect(() => {
    setQty({});
  }, [service?.title]);

  const setItemQty = (idx, val) => {
    setQty((prev) => ({ ...prev, [idx]: Math.max(0, val) }));
  };

  const cart = useMemo(
    () =>
      items
        .map((it, idx) => ({ ...it, idx, quantity: qty[idx] || 0 }))
        .filter((it) => it.quantity > 0),
    [items, qty]
  );

  const total = useMemo(
    () => cart.reduce((sum, it) => sum + (it.quote ? 0 : it.rate * it.quantity), 0),
    [cart]
  );

  const hasQuoteItems = useMemo(() => cart.some((it) => it.quote), [cart]);

  const isConsultation = !!service?.consultation;

  const handleBook = () => {
    if (!cart.length) return;
    let msg = isConsultation
      ? `*Consultation Request - ${service.title}*\n\nI'm interested in the following:\n`
      : `*New Booking - ${service.title}*\n\n`;
    cart.forEach((it, i) => {
      if (it.quote) {
        msg += `${i + 1}. ${it.name}\n   Qty: ${it.quantity} - On Quote\n`;
      } else {
        msg += `${i + 1}. ${it.name}\n   Qty: ${it.quantity} x ${inr(it.rate)} = ${inr(it.rate * it.quantity)}\n`;
      }
    });
    if (isConsultation) {
      msg += `\nPlease schedule a consultation and share an estimate.`;
    } else {
      msg += `\n*Estimated Total: ${inr(total)}*`;
      if (hasQuoteItems) msg += ` (+ On-Quote items)`;
      msg += `\n\n(Rates are indicative; final quote after site assessment.)\nPlease confirm my booking.`;
    }
    window.open(getWhatsAppLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="booking-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b bg-gradient-to-r from-amber-50 to-yellow-50">
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-amber-500" />
            {service?.title} - Book Services
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Choose services & quantities, then send your order to us on WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-0 overflow-hidden flex-1">
          {/* Services list */}
          <div className="overflow-y-auto p-6 space-y-3 border-r">
            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">
              Available Services
            </h3>
            {items.map((it, idx) => (
              <div
                key={idx}
                className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                data-testid={`booking-item-${idx}`}
              >
                <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm leading-tight">{it.name}</p>
                  {it.quote ? (
                    <p className="text-amber-600 font-bold text-sm">
                      On Quote <span className="text-gray-400 font-normal text-xs">/ {it.unit}</span>
                    </p>
                  ) : (
                    <p className="text-amber-600 font-bold text-sm">
                      {inr(it.rate)} <span className="text-gray-400 font-normal text-xs">/ {it.unit}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setItemQty(idx, (qty[idx] || 0) - 1)}
                    className="h-8 w-8 flex items-center justify-center rounded-md bg-white border border-gray-300 hover:bg-gray-100 text-gray-700"
                    data-testid={`booking-minus-${idx}`}
                    aria-label="decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-7 text-center font-bold text-gray-900" data-testid={`booking-qty-${idx}`}>
                    {qty[idx] || 0}
                  </span>
                  <button
                    type="button"
                    onClick={() => setItemQty(idx, (qty[idx] || 0) + 1)}
                    className="h-8 w-8 flex items-center justify-center rounded-md bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid={`booking-plus-${idx}`}
                    aria-label="increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                </div>
                {(it.details || it.warranty) && (
                  <details className="mt-2 group" data-testid={`booking-details-${idx}`}>
                    <summary className="cursor-pointer text-xs font-semibold text-amber-600 hover:text-amber-700 list-none flex items-center gap-1">
                      <Info className="h-3.5 w-3.5" /> Details &amp; warranty
                    </summary>
                    {it.details && (
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">{it.details}</p>
                    )}
                    {it.warranty && (
                      <p className="text-xs text-green-700 mt-1.5 leading-relaxed">
                        <span className="font-semibold">Warranty:</span> {it.warranty}
                      </p>
                    )}
                  </details>
                )}
              </div>
            ))}
          </div>

          {/* Cart */}
          <div className="flex flex-col bg-gray-50/60">
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">
                Your Cart
              </h3>
              {cart.length === 0 ? (
                <p className="text-gray-400 text-sm" data-testid="booking-cart-empty">
                  No services added yet. Use + to add services.
                </p>
              ) : (
                cart.map((it) => (
                  <div
                    key={it.idx}
                    className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg p-3"
                    data-testid={`cart-item-${it.idx}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm leading-tight truncate">{it.name}</p>
                      <p className="text-xs text-gray-500">
                        {it.quantity} x {it.quote ? "On Quote" : inr(it.rate)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-bold text-gray-900 text-sm">{it.quote ? "On Quote" : inr(it.rate * it.quantity)}</span>
                      <button
                        type="button"
                        onClick={() => setItemQty(it.idx, 0)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`cart-remove-${it.idx}`}
                        aria-label="remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Total + book */}
            <div className="border-t p-6 bg-white">
              {isConsultation ? (
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-gray-600 font-medium">Selected services</span>
                  <span className="text-lg font-bold text-amber-600" data-testid="booking-total">
                    {cart.length} item{cart.length === 1 ? "" : "s"}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600 font-medium">Estimated Total</span>
                  <span className="text-2xl font-extrabold text-amber-600" data-testid="booking-total">
                    {inr(total)}
                  </span>
                </div>
              )}
              {isConsultation ? (
                <p className="text-xs text-amber-700 mb-3 -mt-2">
                  Consultation-based pricing - we'll assess your space and share a tailored estimate.
                </p>
              ) : (
                hasQuoteItems && (
                  <p className="text-xs text-amber-700 mb-3 -mt-2" data-testid="booking-quote-note">
                    * Some items are On Quote - final price shared after site assessment.
                  </p>
                )
              )}
              <button
                type="button"
                onClick={handleBook}
                disabled={cart.length === 0}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded-lg transition-colors"
                data-testid="booking-book-btn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {isConsultation ? "Request Consultation on WhatsApp" : "Book on WhatsApp"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceBookingDialog;
